/**
 * Trusscore Estimator - Dimension Calculations
 *
 * Calculates core dimensions for walls and surfaces, including
 * SlatWall zones, garage door cutouts, and net lengths.
 *
 * Ported from: calculateCoreDimensions_IndividualWall() in estimator.js
 */

import {
  CoreDimensions,
  WallConfig,
  SlatWallConfig,
  GarageConfig,
  RoomDimensions,
  FullRoomWallConfig,
} from '../types';
import {
  SLATWALL_COVERAGE_HEIGHT,
  SLATWALL_TRANSITION_SPACE_FEET,
} from './constants';

/**
 * Calculate core dimensions for an individual wall.
 *
 * This is a direct port of calculateCoreDimensions_IndividualWall from estimator.js
 *
 * @param wallConfig - Configuration for the wall
 * @param commonRoomHeight - The room's ceiling height
 * @returns Core dimensions for material calculations
 */
export function calculateCoreDimensions(
  wallConfig: WallConfig,
  commonRoomHeight: number
): CoreDimensions {
  // Calculate garage door total width
  const garageWidthOnWall =
    wallConfig.garage.enabled && wallConfig.garage.width > 0 && wallConfig.garage.numDoors > 0
      ? wallConfig.garage.width * wallConfig.garage.numDoors
      : 0;

  // Net wall length after subtracting garage doors
  const wallNetLength = Math.max(0, wallConfig.length - garageWidthOnWall);

  // Calculate SlatWall dimensions
  let wallheightBelowSW: number;
  let slatWallCalculatedHeight: number;
  let wallheightAboveSW: number;

  if (wallConfig.slatWall.enabled && wallConfig.slatWall.numPanelRows > 0) {
    // SlatWall is enabled
    wallheightBelowSW = wallConfig.slatWall.startHeight;
    slatWallCalculatedHeight = wallConfig.slatWall.numPanelRows * SLATWALL_COVERAGE_HEIGHT;

    // Height above SlatWall = room height - SlatWall start - SlatWall height - transition space
    wallheightAboveSW = Math.max(
      0,
      commonRoomHeight -
        wallheightBelowSW -
        slatWallCalculatedHeight -
        SLATWALL_TRANSITION_SPACE_FEET
    );
  } else {
    // No SlatWall - full height available for W&CB
    wallheightBelowSW = commonRoomHeight;
    slatWallCalculatedHeight = 0;
    wallheightAboveSW = 0;
  }

  // Calculate height above garage door
  const heightAboveGarage =
    wallConfig.garage.enabled && wallConfig.garage.height > 0
      ? Math.max(0, commonRoomHeight - wallConfig.garage.height)
      : 0;

  return {
    wallNetLength,
    wallheightBelowSW,
    slatWallCalculatedHeight,
    wallheightAboveSW,
    heightAboveGarage,
    commonRoomHeight,
  };
}


/**
 * Calculate room perimeter for full room mode.
 *
 * @param roomDimensions - Room length, width, height
 * @returns Perimeter in feet
 */
export function calculateRoomPerimeter(roomDimensions: RoomDimensions): number {
  return 2 * roomDimensions.length + 2 * roomDimensions.width;
}

/**
 * Calculate net perimeter after subtracting garage door widths.
 *
 * @param roomDimensions - Room dimensions
 * @param garageConfig - Garage door configuration
 * @returns Net perimeter in feet
 */
export function calculateNetPerimeter(
  roomDimensions: RoomDimensions,
  garageConfig: GarageConfig & { onWallType: 'None' | 'LengthWall' | 'WidthWall' }
): number {
  const perimeter = calculateRoomPerimeter(roomDimensions);

  if (!garageConfig.enabled || garageConfig.onWallType === 'None') {
    return perimeter;
  }

  const totalGarageWidth = garageConfig.width * garageConfig.numDoors;
  return Math.max(0, perimeter - totalGarageWidth);
}

/**
 * Create wall configurations for all 4 walls in FullRoom mode.
 *
 * @param roomDimensions - Room dimensions
 * @param fullRoomConfig - Full room wall configuration
 * @returns Array of 4 wall configurations
 */
export function createFullRoomWallConfigs(
  roomDimensions: RoomDimensions,
  fullRoomConfig: FullRoomWallConfig
): WallConfig[] {
  const walls: WallConfig[] = [];

  // Determine which wall gets the garage door
  const garageOnLengthWall = fullRoomConfig.garage.onWallType === 'LengthWall';
  const garageOnWidthWall = fullRoomConfig.garage.onWallType === 'WidthWall';

  // Wall 1 (Length A)
  walls.push({
    wallId: 'wall1',
    name: 'Wall 1 (Length A)',
    enabled: true,
    length: roomDimensions.length,
    height: roomDimensions.height,
    color: fullRoomConfig.wallColor,
    orientation: fullRoomConfig.wallOrientation,
    slatWall: { ...fullRoomConfig.slatWall },
    garage: garageOnLengthWall
      ? {
          enabled: fullRoomConfig.garage.enabled,
          width: fullRoomConfig.garage.width,
          height: fullRoomConfig.garage.height,
          numDoors: fullRoomConfig.garage.numDoors,
        }
      : { enabled: false, width: 0, height: 0, numDoors: 0 },
  });

  // Wall 2 (Width B)
  walls.push({
    wallId: 'wall2',
    name: 'Wall 2 (Width B)',
    enabled: true,
    length: roomDimensions.width,
    height: roomDimensions.height,
    color: fullRoomConfig.wallColor,
    orientation: fullRoomConfig.wallOrientation,
    slatWall: { ...fullRoomConfig.slatWall },
    garage: garageOnWidthWall
      ? {
          enabled: fullRoomConfig.garage.enabled,
          width: fullRoomConfig.garage.width,
          height: fullRoomConfig.garage.height,
          numDoors: fullRoomConfig.garage.numDoors,
        }
      : { enabled: false, width: 0, height: 0, numDoors: 0 },
  });

  // Wall 3 (Length C)
  walls.push({
    wallId: 'wall3',
    name: 'Wall 3 (Length C)',
    enabled: true,
    length: roomDimensions.length,
    height: roomDimensions.height,
    color: fullRoomConfig.wallColor,
    orientation: fullRoomConfig.wallOrientation,
    slatWall: { ...fullRoomConfig.slatWall },
    garage: { enabled: false, width: 0, height: 0, numDoors: 0 }, // Garage only on one wall
  });

  // Wall 4 (Width D)
  walls.push({
    wallId: 'wall4',
    name: 'Wall 4 (Width D)',
    enabled: true,
    length: roomDimensions.width,
    height: roomDimensions.height,
    color: fullRoomConfig.wallColor,
    orientation: fullRoomConfig.wallOrientation,
    slatWall: { ...fullRoomConfig.slatWall },
    garage: { enabled: false, width: 0, height: 0, numDoors: 0 }, // Garage only on one wall
  });

  return walls;
}

/**
 * Validate wall dimensions.
 *
 * @param wallConfig - Wall configuration to validate
 * @returns Validation result with any errors
 */
export function validateWallDimensions(wallConfig: WallConfig): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (wallConfig.length <= 0) {
    errors.push(`Wall ${wallConfig.wallId}: Length must be greater than 0`);
  }

  if (wallConfig.height <= 0) {
    errors.push(`Wall ${wallConfig.wallId}: Height must be greater than 0`);
  }

  if (wallConfig.garage.enabled) {
    if (wallConfig.garage.width * wallConfig.garage.numDoors >= wallConfig.length) {
      errors.push(
        `Wall ${wallConfig.wallId}: Garage door width exceeds wall length`
      );
    }
    if (wallConfig.garage.height >= wallConfig.height) {
      errors.push(
        `Wall ${wallConfig.wallId}: Garage door height exceeds wall height`
      );
    }
  }

  if (wallConfig.slatWall.enabled) {
    const slatWallHeight = wallConfig.slatWall.numPanelRows * SLATWALL_COVERAGE_HEIGHT;
    const totalSlatWallSpace =
      wallConfig.slatWall.startHeight + slatWallHeight + SLATWALL_TRANSITION_SPACE_FEET;

    if (totalSlatWallSpace > wallConfig.height) {
      errors.push(
        `Wall ${wallConfig.wallId}: SlatWall configuration exceeds wall height`
      );
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export default {
  calculateCoreDimensions,
  calculateRoomPerimeter,
  calculateNetPerimeter,
  createFullRoomWallConfigs,
  validateWallDimensions,
};
